import React, { Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import Sidebar from '@/components/app/Sidebar';
import BottomNav from '@/components/app/BottomNav';
import LeaderboardIntro from '@/components/app/LeaderboardIntro';
import MobileHeader from '@/components/app/MobileHeader';
import PageSkeleton from '@/components/app/PageSkeleton';
import Topography from '@/components/reactbits/Topography';

/**
 * The chrome, separated from the routed content so the loading state can render
 * the *same* shell instead of a bare page that the sidebar then pops into.
 */
export function AppShell({ children }) {
  return (
    <div className="relative min-h-[100dvh] bg-dd-bg">
      {/*
        Contour backdrop — a map texture under a driving app. Fixed and
        non-interactive, fading out toward the bottom so it never competes with
        the content. Tuned dark: at full strength it would read as decoration.
      */}
      <div
        className="fixed inset-0 z-0 pointer-events-none"
        aria-hidden="true"
        style={{
          maskImage: 'radial-gradient(130% 95% at 50% 0%, #000 30%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(130% 95% at 50% 0%, #000 30%, transparent 100%)',
        }}
      >
        <Topography
          lowColor="#0A0D09"
          midColor="#3F5A28"
          highColor="#CCFF84"
          speed={0.12}
          morphSpeed={0.02}
          morphAmount={2.2}
          bands={2.0}
          thickness={0.012}
          scale={1.35}
          glow={0.35}
          contrast={2.2}
          brightness={0.8}
          opacity={0.16}
          grain
          grainIntensity={0.03}
        />
      </div>

      <div className="relative z-10">
        <Sidebar />
        <MobileHeader />

        {/* Offset matches the sidebar's two widths; nothing sits under it. */}
        <div className="md:pl-[72px] lg:pl-[240px]">
          {/* The bottom bar is fixed and its add-ride button sits 24px proud of
              it, so the page reserves bar + button + the home-indicator inset
              rather than letting content slide underneath. */}
          <main className="pb-[calc(58px+24px+env(safe-area-inset-bottom)+1.5rem)] md:pb-10">
            {children}
          </main>
        </div>

        <BottomNav />
        <LeaderboardIntro />
      </div>
    </div>
  );
}

export default function AppLayout() {
  return (
    <AppShell>
      {/*
        Pages are code-split, so navigating to one that has not been fetched
        suspends here. The boundary sits inside the shell on purpose: the
        sidebar and nav stay put and only the body shows the skeleton, which is
        the same trade the loading state above already makes.
      */}
      <Suspense fallback={<PageSkeleton />}>
        <Outlet />
      </Suspense>
    </AppShell>
  );
}
